import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { useAuth } from "../contexts/AuthContext";

const roleHome = {
  admin: "/admin",
  driver: "/driver",
  school: "/school",
  parent: "/parent",
};

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, changePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Complete all password fields to continue.");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Choose a password with at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("The new passwords do not match.");
      return;
    }

    setSubmitting(true);
    const result = await changePassword(currentPassword, newPassword);
    setSubmitting(false);

    if (result.success) {
      toast.success("Password updated successfully.");
      navigate(roleHome[user?.role] || "/");
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(180deg,#F8FAFC_0%,#EEF2FF_100%)] px-6 py-12">
      <div className="grid w-full max-w-6xl overflow-hidden rounded-[36px] border border-[#E2E8F0] bg-white shadow-[0_24px_80px_rgba(15,23,42,0.12)] lg:grid-cols-[1.05fr_0.95fr]">
        <div className="p-8 lg:p-12">
          <Logo size="large" />
          <div className="mt-8 max-w-xl space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800">
              <ShieldCheck size={14} />
              First sign-in security check
            </div>
            <h1 className="font-['Manrope'] text-4xl font-semibold tracking-tight text-[#0F172A]">
              Update your password before continuing
            </h1>
            <p className="text-base leading-7 text-[#475569]">
              To protect access to the CareFleet Guardian platform, your organisation requires a new password the first time you sign in.
            </p>
          </div>

          <form className="mt-10 max-w-xl space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Current password</label>
              <div className="relative mt-2">
                <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  className="w-full rounded-2xl border border-[#CBD5E1] bg-white px-10 py-3 text-sm outline-none transition focus:border-[#163D6B] focus:ring-4 focus:ring-[#163D6B]/10"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">New password</label>
              <div className="relative mt-2">
                <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="w-full rounded-2xl border border-[#CBD5E1] bg-white px-10 py-3 text-sm outline-none transition focus:border-[#163D6B] focus:ring-4 focus:ring-[#163D6B]/10"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Confirm new password</label>
              <div className="relative mt-2">
                <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="w-full rounded-2xl border border-[#CBD5E1] bg-white px-10 py-3 text-sm outline-none transition focus:border-[#163D6B] focus:ring-4 focus:ring-[#163D6B]/10"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center rounded-2xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#0E2E53] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Updating password..." : "Save new password"}
            </button>
            <button
              type="button"
              onClick={logout}
              className="inline-flex w-full items-center justify-center rounded-2xl border border-[#CBD5E1] bg-white px-4 py-3 text-sm font-semibold text-[#0F172A]"
            >
              Sign out instead
            </button>
          </form>
        </div>

        <div className="hidden bg-[linear-gradient(180deg,#163D6B_0%,#0E2E53_100%)] p-12 text-white lg:block">
          <div className="rounded-[32px] border border-white/15 bg-white/10 p-10 backdrop-blur-md">
            <p className="text-sm uppercase tracking-[0.28em] text-white/70">Account protection</p>
            <h2 className="mt-4 font-['Manrope'] text-4xl font-semibold tracking-tight">
              Secure access for every role across the service
            </h2>
            <ul className="mt-8 space-y-4 text-sm text-white/85">
              <li>• Supports administrators, drivers, schools, and parents</li>
              <li>• Helps protect journey data and safeguarding records</li>
              <li>• Ensures authorised access before daily operations begin</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
